import { SITE_URL, localePath, htmlLang, type Lang as SiteLang } from './i18n';
import { equipmentLabel } from '../content/exercises/equipmentLabels';
import {
  EXERCISES,
  TECHNIQUES_MIN,
  type ExerciseRecord,
  type ExerciseTranslation,
  type TechniqueMin,
  type Lang,
} from '../content/exercises/data.generated';

import seoEn from '../content/exercises/seo-content.en.json';
import seoRu from '../content/exercises/seo-content.ru.json';
import seoDe from '../content/exercises/seo-content.de.json';
import seoFr from '../content/exercises/seo-content.fr.json';
import seoEs from '../content/exercises/seo-content.es.json';
import seoPtBR from '../content/exercises/seo-content.pt-BR.json';
import seoJa from '../content/exercises/seo-content.ja.json';
import seoKo from '../content/exercises/seo-content.ko.json';

export { equipmentLabel };
export type { ExerciseRecord, ExerciseTranslation, TechniqueMin };
export const exercisesBase = (lang: SiteLang) => localePath(lang, '/armwrestling/exercises');

export interface FaqItem {
  q: string;
  a: string;
}

export interface SeoContent {
  seoIntro?: string;
  commonMistakes?: string[];
  metaDescription?: string;
  faq?: FaqItem[];
}

const seoByLocale: Record<Lang, Record<string, SeoContent>> = {
  en: seoEn as Record<string, SeoContent>,
  ru: seoRu as Record<string, SeoContent>,
  de: seoDe as Record<string, SeoContent>,
  fr: seoFr as Record<string, SeoContent>,
  es: seoEs as Record<string, SeoContent>,
  'pt-BR': seoPtBR as Record<string, SeoContent>,
  ja: seoJa as Record<string, SeoContent>,
  ko: seoKo as Record<string, SeoContent>,
};

// Human-reviewed SEO copy (seo-content.<lang>.json) falls back to English when
// a given exercise/locale pair hasn't been authored yet — English "extra"
// content shown alongside a fully native-language body (name/description/
// instructions all come from the reused, always-native `translations` field)
// is an acceptable interim state, unlike faking a whole page as if native.
function seoFor(id: string, lang: Lang): SeoContent {
  return seoByLocale[lang]?.[id] || seoByLocale.en[id] || {};
}

export interface ResolvedExercise {
  id: string;
  slug: string;
  emoji: string;
  category: ExerciseRecord['category'];
  equipmentKeys: string[];
  difficulty: ExerciseRecord['difficulty'];
  hasVideo: boolean;
  relatedTechniqueIds: string[];
  armGroup: string | null;
  t: ExerciseTranslation;
  seo: SeoContent;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  imageUrl: string;
}

const MEDIA_BASE = 'https://app.mcdevlabs.com/media';
// Exercise videos self-hosted under public/videos/exercises/{id}.mp4 (pulled from
// the ArmProgress media server). A self-hosted id plays its local video even if
// the generated data marks it hasVideo:false (e.g. wristCircles, whose clip only
// exists on our server, not the armwrestling CDN). Everything else with a video
// streams from the CDN. Add an id here after dropping its .mp4 into public/videos/exercises/.
const LOCAL_VIDEOS = new Set<string>(['wristCircles']);
export const videoUrlForExercise = (ex: Pick<ExerciseRecord, 'id' | 'hasVideo'>): string | null => {
  if (LOCAL_VIDEOS.has(ex.id)) return `/videos/exercises/${ex.id}.mp4`;
  return ex.hasVideo ? `${MEDIA_BASE}/videos/armwrestling/${ex.id}.mp4` : null;
};
export const thumbnailUrlForExercise = (ex: Pick<ExerciseRecord, 'id' | 'hasVideo'>): string | null =>
  ex.hasVideo ? `${MEDIA_BASE}/thumbs/armwrestling/${ex.id}.jpg` : null;
// Self-hosted card/hero thumbnail — a frame extracted from the exercise video
// (scripts/extract-exercise-thumbs.mjs) or a generated tile for the no-video
// ones. Missing files fall back to the emoji tile via <Thumb>.
export const imageUrlForExercise = (ex: Pick<ExerciseRecord, 'id'>): string =>
  `/images/exercises/${ex.id}.jpg`;

// Only exercises that actually have a real (non-fallback) translation for
// `lang` are considered "available" in that locale — this is what keeps a
// locale's index/sitemap/hreflang honest instead of publishing an
// English-in-disguise page under a native-language URL.
export function getExercises(lang: Lang): ResolvedExercise[] {
  return EXERCISES.filter((e) => e.translations[lang]).map((e) => resolve(e, lang));
}

export function getExercise(lang: Lang, slug: string): ResolvedExercise | undefined {
  const e = EXERCISES.find((x) => x.slug === slug && x.translations[lang]);
  return e ? resolve(e, lang) : undefined;
}

// All locales in which this exercise id has a real translation (for hreflang
// alternates) — slugs are locale-invariant, so this is really just "which
// locales include this id."
export function localesForExercise(id: string): Lang[] {
  const e = EXERCISES.find((x) => x.id === id);
  if (!e) return [];
  return (Object.keys(e.translations) as Lang[]).filter((l) => e.translations[l]);
}

function resolve(e: ExerciseRecord, lang: Lang): ResolvedExercise {
  return {
    id: e.id,
    slug: e.slug,
    emoji: e.emoji,
    category: e.category,
    equipmentKeys: e.equipmentKeys,
    difficulty: e.difficulty,
    hasVideo: e.hasVideo,
    relatedTechniqueIds: e.relatedTechniqueIds,
    armGroup: e.armGroup,
    t: e.translations[lang]!,
    seo: seoFor(e.id, lang),
    videoUrl: videoUrlForExercise(e),
    thumbnailUrl: thumbnailUrlForExercise(e),
    imageUrl: imageUrlForExercise(e),
  };
}

// "Home-friendly" = doable without a gym or specialized armwrestling club
// equipment (cable machine, EZ bar, riser tool, multi-spinner). Real,
// validated demand for this filter: Google Trends shows "arm wrestling
// exercises at home" as a rising related query for "arm wrestling exercises"
// (+10%, also on the Rising panel) — this is a filtered slice of the same
// exercise data, not a new content cluster.
const NON_HOME_EQUIPMENT = new Set(['cable', 'ezBar', 'pullupBar', 'riser', 'multispinner', 'barbell']);
export function isHomeFriendly(equipmentKeys: string[]): boolean {
  return equipmentKeys.every((k) => !NON_HOME_EQUIPMENT.has(k));
}

export function getTechniqueName(id: string, lang: Lang): string {
  const t = TECHNIQUES_MIN.find((x) => x.id === id);
  return t?.names[lang] || t?.names.en || id;
}

// Related exercises ranked by: shared technique > shared muscle group >
// shared difficulty. Caps at 6, excludes self, dedupes.
export function relatedExercises(ex: ResolvedExercise, lang: Lang, limit = 6): ResolvedExercise[] {
  const pool = getExercises(lang).filter((o) => o.id !== ex.id);
  const score = (o: ResolvedExercise) => {
    const sharedTechnique = o.relatedTechniqueIds.some((t) => ex.relatedTechniqueIds.includes(t));
    const sharedGroup = !!ex.armGroup && o.armGroup === ex.armGroup;
    const sharedDifficulty = o.difficulty === ex.difficulty;
    return (sharedTechnique ? 4 : 0) + (sharedGroup ? 2 : 0) + (sharedDifficulty ? 1 : 0);
  };
  return pool
    .map((o) => ({ o, s: score(o) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.o);
}

export function alternatesForExercise(id: string): { hreflang: string; href: string }[] {
  const locales = localesForExercise(id);
  const e = EXERCISES.find((x) => x.id === id)!;
  const out = locales.map((l) => ({
    hreflang: htmlLang[l],
    href: `${SITE_URL}${localePath(l, `/armwrestling/exercises/${e.slug}`)}`,
  }));
  if (locales.includes('en')) {
    out.push({ hreflang: 'x-default', href: `${SITE_URL}${localePath('en', `/armwrestling/exercises/${e.slug}`)}` });
  }
  return out;
}
